/**
 * V97: 고객사(Customer) 마스터 관리 Hooks
 *
 * useCustomers: 고객사 목록 조회
 * useCustomerMutations: 고객사 생성/수정/아카이브
 * useCustomerProjects: 고객사별 프로젝트 목록 조회
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  Customer,
  ListCustomersInput,
  ListCustomersOutput,
  CreateCustomerInput,
  CreateCustomerOutput,
  UpdateCustomerInput,
  UpdateCustomerOutput,
  ArchiveCustomerInput,
  ArchiveCustomerOutput,
  GetCustomerProjectsInput,
  GetCustomerProjectsOutput,
  CustomerProjectSummary,
} from '../types/customer';

// ============================================
// useCustomers Hook
// ============================================

export interface UseCustomersOptions {
  /** 회사 코드 필터 */
  companyCode?: 'KR' | 'CN';
  /** archived 포함 여부 */
  includeArchived?: boolean;
  /** 자동 조회 여부 (기본: true) */
  autoFetch?: boolean;
}

export interface UseCustomersResult {
  customers: Customer[];
  isLoading: boolean;
  error: Error | null;
  scope: 'KR' | 'CN' | 'ALL' | null;
  refetch: () => Promise<void>;
}

/**
 * 고객사 목록 조회 Hook
 */
export function useCustomers(options?: UseCustomersOptions): UseCustomersResult {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [scope, setScope] = useState<'KR' | 'CN' | 'ALL' | null>(null);

  const { companyCode, includeArchived = false, autoFetch = true } = options || {};

  const fetchCustomers = useCallback(async () => {
    if (!functions) {
      setCustomers([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const listCustomersFn = httpsCallable<ListCustomersInput, ListCustomersOutput>(
        functions,
        'listCustomers'
      );

      const result = await listCustomersFn({
        companyCode,
        includeArchived,
      });

      if (result.data.success) {
        setCustomers(result.data.customers);
        setScope(result.data.scope);
      } else {
        throw new Error('고객사 목록 조회 실패');
      }
    } catch (err: any) {
      console.error('[useCustomers] 조회 오류:', err);
      setError(err instanceof Error ? err : new Error(err?.message || '알 수 없는 오류'));
      setCustomers([]);
    } finally {
      setIsLoading(false);
    }
  }, [companyCode, includeArchived]);

  useEffect(() => {
    if (autoFetch) {
      fetchCustomers();
    }
  }, [autoFetch, fetchCustomers]);

  return {
    customers,
    isLoading,
    error,
    scope,
    refetch: fetchCustomers,
  };
}

// ============================================
// useCustomerMutations Hook
// ============================================

export interface UseCustomerMutationsResult {
  createCustomer: (input: CreateCustomerInput) => Promise<string>;
  updateCustomer: (input: UpdateCustomerInput) => Promise<void>;
  archiveCustomer: (id: string) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * 고객사 생성/수정/아카이브 Hook
 */
export function useCustomerMutations(): UseCustomerMutationsResult {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const createCustomer = useCallback(async (input: CreateCustomerInput): Promise<string> => {
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    try {
      setIsLoading(true);
      setError(null);

      const createCustomerFn = httpsCallable<CreateCustomerInput, CreateCustomerOutput>(
        functions,
        'createCustomer'
      );

      const result = await createCustomerFn(input);

      if (result.data.success) {
        return result.data.customerId;
      } else {
        throw new Error('고객사 생성 실패');
      }
    } catch (err: any) {
      console.error('[useCustomerMutations] 생성 오류:', err);
      const error = err instanceof Error ? err : new Error(err?.message || '고객사 생성 중 오류');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateCustomer = useCallback(async (input: UpdateCustomerInput): Promise<void> => {
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    try {
      setIsLoading(true);
      setError(null);

      const updateCustomerFn = httpsCallable<UpdateCustomerInput, UpdateCustomerOutput>(
        functions,
        'updateCustomer'
      );

      const result = await updateCustomerFn(input);

      if (result.data.success) {
      } else {
        throw new Error('고객사 수정 실패');
      }
    } catch (err: any) {
      console.error('[useCustomerMutations] 수정 오류:', err);
      const error = err instanceof Error ? err : new Error(err?.message || '고객사 수정 중 오류');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const archiveCustomer = useCallback(async (id: string): Promise<void> => {
    if (!functions) {
      throw new Error('Firebase Functions not initialized');
    }

    try {
      setIsLoading(true);
      setError(null);

      const archiveCustomerFn = httpsCallable<ArchiveCustomerInput, ArchiveCustomerOutput>(
        functions,
        'archiveCustomer'
      );

      const result = await archiveCustomerFn({ id });

      if (result.data.success) {
      } else {
        throw new Error('고객사 아카이브 실패');
      }
    } catch (err: any) {
      console.error('[useCustomerMutations] 아카이브 오류:', err);
      const error = err instanceof Error ? err : new Error(err?.message || '고객사 아카이브 중 오류');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    createCustomer,
    updateCustomer,
    archiveCustomer,
    isLoading,
    error,
  };
}

// ============================================
// useCustomerProjects Hook
// ============================================

export interface UseCustomerProjectsOptions {
  customerId: string;
  status?: 'active' | 'completed' | 'onHold' | 'cancelled';
  autoFetch?: boolean;
}

export interface UseCustomerProjectsResult {
  projects: CustomerProjectSummary[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * 고객사별 프로젝트 목록 조회 Hook
 */
export function useCustomerProjects(options: UseCustomerProjectsOptions): UseCustomerProjectsResult {
  const [projects, setProjects] = useState<CustomerProjectSummary[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const { customerId, status, autoFetch = true } = options;

  const fetchProjects = useCallback(async () => {
    if (!functions) {
      setProjects([]);
      setIsLoading(false);
      return;
    }

    if (!customerId) {
      setProjects([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const getCustomerProjectsFn = httpsCallable<GetCustomerProjectsInput, GetCustomerProjectsOutput>(
        functions,
        'getCustomerProjects'
      );

      const result = await getCustomerProjectsFn({
        customerId,
        status,
      });

      if (result.data.success) {
        setProjects(result.data.projects);
      } else {
        throw new Error('고객사별 프로젝트 조회 실패');
      }
    } catch (err: any) {
      console.error('[useCustomerProjects] 조회 오류:', err);
      setError(err instanceof Error ? err : new Error(err?.message || '알 수 없는 오류'));
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  }, [customerId, status]);

  useEffect(() => {
    if (autoFetch && customerId) {
      fetchProjects();
    }
  }, [autoFetch, customerId, fetchProjects]);

  return {
    projects,
    isLoading,
    error,
    refetch: fetchProjects,
  };
}

export default useCustomers;
